import { alpha, type Components, type Theme } from '@mui/material/styles';
// Side-effect import: augments MUI Components<> with @mui/x-date-pickers slots
// (MuiPickersOutlinedInput, MuiPickersInputBase, MuiPickersTextField, etc.)
// so the overrides below typecheck without `any` casts.
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import { semantic } from './tokens/semantic';
import { color, fontSize, fontWeight, lineHeight, radius } from './tokens/primitives';

// Register the `inset` Card variant (a nested darker card painted inside a
// white card — used by `VyToggleSection`'s body slot and consumers that need
// the same surface treatment elsewhere). In MUI v9 `Card` inherits its
// variant prop from `Paper`, so the augmentation lives on Paper's slot.
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    inset: true;
  }
}

/**
 * Shared visual shell for outlined field roots. `@mui/material`'s
 * `MuiOutlinedInput` and `@mui/x-date-pickers`' `MuiPickersOutlinedInput`
 * are separate slots with differently-named notched-outline classes, so the
 * caller passes the class name; everything else (radius, font, surface,
 * hover/focus/error/disabled border states) stays in sync from one place.
 */
const outlinedFieldShell = (theme: Theme, notchedOutlineClass: string) => ({
  borderRadius: radius.sm,
  fontFamily: theme.typography.fontFamily,
  fontSize: fontSize[13],
  backgroundColor: theme.palette.background.paper,
  [`& .${notchedOutlineClass}`]: {
    borderColor: theme.palette.divider,
  },
  [`&:hover .${notchedOutlineClass}`]: {
    borderColor: theme.palette.text.secondary,
  },
  [`&.Mui-focused .${notchedOutlineClass}`]: {
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
  },
  [`&.Mui-error .${notchedOutlineClass}`]: {
    borderColor: theme.palette.error.main,
  },
  // No grey fill on disabled — only the outline/text dim.
});

/**
 * MUI primitive overrides. Sources values from `semantic` where Figma
 * prescribes a semantic token, otherwise from `theme.*` keys. Never inline
 * hex. Adding a new override is the right move when a consumer would
 * otherwise need to reach for `sx`.
 */
export const components: Components<Omit<Theme, 'components'>> = {
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        textTransform: 'none',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.button?.fontSize,
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: theme.typography.button?.lineHeight,
        paddingInline: theme.spacing(3),
        paddingBlock: theme.spacing(1.5),
        boxShadow: 'none',
        '&:focus-visible': {
          outline: `2px solid ${semantic.button.primary.focused}`,
          outlineOffset: 2,
        },
      }),
      contained: () => ({
        backgroundColor: semantic.button.primary.idle,
        color: semantic.button.text.idle,
        '&:hover': {
          backgroundColor: semantic.button.primary.hover,
          boxShadow: 'none',
        },
        '&:focus-visible': {
          backgroundColor: semantic.button.primary.focused,
        },
        '&.Mui-disabled': {
          backgroundColor: semantic.button.primary.disabled,
          color: semantic.button.text.idle,
        },
      }),
      outlined: ({ theme }) => ({
        borderColor: alpha(semantic.button.primary.idle, 0.5),
        color: semantic.button.primary.idle,
        backgroundColor: 'transparent',
        '&:hover': {
          borderColor: semantic.button.primary.idle,
          backgroundColor: alpha(semantic.button.primary.idle, 0.04),
        },
        '&.Mui-disabled': {
          borderColor: theme.palette.action.disabled,
          color: theme.palette.action.disabled,
        },
      }),
      text: ({ theme }) => ({
        color: theme.palette.text.secondary,
        paddingInline: theme.spacing(2),
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
        },
        '&.Mui-disabled': {
          color: theme.palette.action.disabled,
        },
      }),
      sizeSmall: ({ theme }) => ({
        fontSize: theme.typography.body2?.fontSize,
        paddingInline: theme.spacing(2),
        paddingBlock: theme.spacing(1),
      }),
      sizeLarge: ({ theme }) => ({
        fontSize: theme.typography.subtitle1?.fontSize,
        paddingInline: theme.spacing(4),
        paddingBlock: theme.spacing(2),
      }),
    },
    variants: [
      {
        props: { variant: 'contained', color: 'secondary' },
        style: {
          backgroundColor: semantic.button.secondary.idle,
          color: color.neutrals[900],
          '&:hover': { backgroundColor: semantic.button.secondary.hover },
          '&:focus-visible': { backgroundColor: semantic.button.secondary.focused },
          '&.Mui-disabled': {
            backgroundColor: semantic.button.secondary.disabled,
            color: color.neutrals[400],
          },
        },
      },
      {
        props: { variant: 'text', color: 'info' },
        style: {
          color: color.vayuBlue.action,
          '&:hover': {
            backgroundColor: alpha(color.vayuBlue.action, 0.04),
            color: color.vayuBlue.action,
          },
        },
      },
      // Destructive (color="error") across the three emphases. Additive — the
      // primary navy treatments above are untouched.
      {
        props: { variant: 'contained', color: 'error' },
        style: {
          backgroundColor: color.alert[900],
          color: color.base.white,
          '&:hover': { backgroundColor: color.alert[950] },
          '&.Mui-disabled': {
            backgroundColor: color.alert[200],
            color: color.base.white,
          },
        },
      },
      {
        props: { variant: 'outlined', color: 'error' },
        style: {
          borderColor: alpha(color.alert[900], 0.5),
          color: color.alert[900],
          backgroundColor: 'transparent',
          '&:hover': {
            borderColor: color.alert[900],
            backgroundColor: alpha(color.alert[900], 0.04),
          },
        },
      },
      {
        props: { variant: 'text', color: 'error' },
        style: {
          color: color.alert[900],
          '&:hover': {
            backgroundColor: alpha(color.alert[900], 0.04),
            color: color.alert[950],
          },
        },
      },
    ],
  },

  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
        },
      }),
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        fontWeight: theme.typography.fontWeightMedium,
      }),
    },
    variants: [
      {
        props: { color: 'error' },
        style: {
          backgroundColor: semantic.tags.alert.bg,
          color: semantic.tags.alert.text,
        },
      },
      {
        props: { color: 'info' },
        style: {
          backgroundColor: semantic.tags.info.bg,
          color: semantic.tags.info.text,
        },
      },
      {
        props: { color: 'success' },
        style: {
          backgroundColor: semantic.tags.success.bg,
          color: semantic.tags.success.text,
        },
      },
    ],
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: semantic.dropdown.menuItem.primary,
        color: theme.palette.text.primary,
        '&:hover': {
          backgroundColor: semantic.dropdown.menuItem.hover,
        },
        '&.Mui-selected, &:focus-visible': {
          backgroundColor: semantic.dropdown.menuItem.focused,
        },
        '&.Mui-selected:hover': {
          backgroundColor: semantic.dropdown.menuItem.focused,
        },
      }),
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: () => ({
        borderColor: semantic.divider.default,
      }),
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
      fullWidth: true,
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => outlinedFieldShell(theme, 'MuiOutlinedInput-notchedOutline'),
      input: ({ theme }) => ({
        paddingBlock: theme.spacing(1),
        paddingInline: theme.spacing(1.5),
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily: theme.typography.fontFamily,
        fontSize: fontSize['12_5'],
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
          color: theme.palette.primary.main,
        },
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      // Matches the `fieldHelper` typography variant (11.5 / Gray Chateau / 16.1)
      // so raw MUI helper text stays in sync with LabeledField's helper.
      root: ({ theme }) => ({
        fontFamily: theme.typography.fontFamily,
        fontSize: fontSize['11_5'],
        lineHeight: lineHeight['16_1'],
        marginInline: 0,
        marginTop: theme.spacing(0.5),
        color: color.neutrals[450],
        '&.Mui-error': {
          color: theme.palette.error.main,
        },
      }),
    },
  },

  // Picker text field — `@mui/x-date-pickers` uses its own `MuiPickersInputBase`
  // / `MuiPickersOutlinedInput` shell, not `MuiOutlinedInput`. Match the small
  // outlined-input look so date pickers don't render taller / differently
  // bordered than `VyTextField` / `VySelect`.
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => outlinedFieldShell(theme, 'MuiPickersOutlinedInput-notchedOutline'),
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily: theme.typography.fontFamily,
        fontSize: fontSize[13],
      }),
      sectionsContainer: ({ theme }) => ({
        paddingBlock: theme.spacing(1),
        paddingInline: theme.spacing(1.5),
        fontSize: fontSize[13],
        lineHeight: lineHeight[20],
      }),
    },
  },

  MuiAutocomplete: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      inputRoot: ({ theme }) => ({
        paddingBlock: 0,
        paddingInlineEnd: theme.spacing(4),
      }),
      paper: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        marginBlockStart: theme.spacing(0.5),
        boxShadow: theme.shadows[3],
      }),
      option: ({ theme }) => ({
        fontFamily: theme.typography.fontFamily,
        fontSize: fontSize[13],
      }),
    },
  },

  MuiSwitch: {
    defaultProps: {
      disableRipple: true,
      size: 'small',
    },
  },

  // Selection controls — checked/selected fill is the action navy
  // (`primary.main` = vayuBlue/950), the check/dot is the inverse content
  // (white), the resting box/ring is the strong neutral border, and focus
  // shows the bright accent ring. Matches Figma `<Checkbox>` / `<Radio>`.
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      size: 'small',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        padding: theme.spacing(0.75),
        borderRadius: radius.sm,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
          color: theme.palette.primary.main,
        },
        '&.Mui-disabled': {
          color: theme.palette.action.disabled,
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${semantic.accent.focus}`,
          outlineOffset: 1,
          borderRadius: radius.sm,
        },
      }),
    },
  },

  MuiRadio: {
    defaultProps: {
      disableRipple: true,
      size: 'small',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        padding: theme.spacing(0.75),
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        '&.Mui-checked': {
          color: theme.palette.primary.main,
        },
        '&.Mui-disabled': {
          color: theme.palette.action.disabled,
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${semantic.accent.focus}`,
          outlineOffset: 1,
          borderRadius: '50%',
        },
      }),
    },
  },

  MuiToggleButtonGroup: {
    styleOverrides: {
      // Native segmented look: each button keeps its own rounded outer corners;
      // square only the shared inner corners and merge the middle border.
      grouped: ({ theme }) => ({
        '&:not(:last-of-type)': {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        },
        '&:not(:first-of-type)': {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          marginLeft: '-1px',
          borderLeft: `1px solid ${theme.palette.divider}`,
        },
      }),
    },
  },

  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: radius.sm,
        textTransform: 'none',
        fontFamily: theme.typography.fontFamily,
        fontSize: fontSize[12],
        fontWeight: fontWeight.medium,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.background.paper,
        paddingBlock: theme.spacing(1),
        paddingInline: theme.spacing(1.5),
        '&.Mui-selected': {
          backgroundColor: color.vayuBlue.action,
          color: theme.palette.primary.contrastText,
          '&:hover': { backgroundColor: color.vayuBlue.action },
        },
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }),
    },
  },

  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: semantic.bg.cardPrimary,
        borderRadius: radius.lg,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        boxShadow: semantic.shadow.base,
        transition: theme.transitions.create(['box-shadow'], {
          duration: theme.transitions.duration.shortest,
        }),
        // Pin the last child to the bottom — so a footer (e.g. status text)
        // sits at the bottom edge regardless of card height. Consumers
        // compose `[icon, body, footer]` and the footer auto-aligns.
        '& > :last-child': {
          marginTop: 'auto',
        },
        '&:hover': {
          boxShadow: semantic.shadow.md,
        },
        // Interactive affordance — matches both `<VyCard interactive>` (which
        // adds role="button") and `<VyCard component="a" href=…>` (anchor).
        // The anchor reset (textDecoration/color) is harmless on a div, so
        // we apply it unconditionally inside the selector.
        '&[role="button"], &[href]': {
          cursor: 'pointer',
          textDecoration: 'none',
          color: 'inherit',
          '&:focus-visible': {
            outline: `2px solid ${semantic.button.primary.focused}`,
            outlineOffset: 2,
          },
        },
      }),
    },
    variants: [
      {
        // Inset card — the darker nested card painted *inside* a white card.
        // No shadow, lighter neutral surface, thin subtle border, slightly
        // smaller radius. Used by `VyToggleSection`'s body slot and the
        // Installments Breakdown panel.
        props: { variant: 'inset' },
        style: ({ theme }: { theme: Theme }) => ({
          backgroundColor: semantic.bg.cardInset,
          border: `1px solid ${semantic.divider.subtle}`,
          borderRadius: radius.lg,
          padding: theme.spacing(2.5),
          gap: theme.spacing(2),
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '& > :last-child': { marginTop: 0 },
        }),
      },
    ],
  },

  MuiTableContainer: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
      }),
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottomColor: theme.palette.divider,
        fontFamily: theme.typography.fontFamily,
      }),
      head: ({ theme }) => ({
        color: theme.palette.text.secondary,
        fontSize: fontSize[12],
        fontWeight: fontWeight.semibold,
        lineHeight: lineHeight[24],
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1.25, 2),
      }),
      body: ({ theme }) => ({
        padding: theme.spacing(0.75, 1.5),
        color: theme.palette.text.primary,
      }),
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:last-child .MuiTableCell-body': { borderBottom: 'none' },
      },
    },
  },

  // ── Feedback: VyDialog ─────────────────────────────────────────────────────

  MuiDialog: {
    defaultProps: {
      maxWidth: 'md',
    },
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: radius.lg,
        boxShadow: theme.shadows[8],
      }),
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2.5),
        fontSize: fontSize[20],
        fontWeight: fontWeight.medium,
        lineHeight: lineHeight[24],
        fontFamily: theme.typography.fontFamily,
      }),
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2.5),
        fontFamily: theme.typography.fontFamily,
      }),
      dividers: ({ theme }) => ({
        borderTopColor: theme.palette.divider,
        borderBottomColor: theme.palette.divider,
      }),
    },
  },

  /**
   * Default visual treatment for the progress bar track and bar fill.
   * Per-instance overrides (`progressColor`, `progressHeight`) are applied
   * by the `StyledLinearProgress` wrapper in `VyLinearProgress.tsx` via
   * transient props — these theme defaults apply to the un-customized case.
   *
   * Track background token: `color.neutrals[900]` at ~6% opacity.
   * The hex `#0D1B270F` from the legacy app maps to `color.neutrals[900]`
   * (`#020F20`) at 6% alpha (0x0F / 0xFF ≈ 5.9%). `color.neutrals[900]`
   * is the nearest named neutral token in primitives.ts.
   */
  MuiLinearProgress: {
    defaultProps: {
      variant: 'determinate',
    },
    styleOverrides: {
      root: () => ({
        height: '6px',
        borderRadius: radius.lg,
        backgroundColor: `${color.neutrals[900]}0F`,
      }),
      bar: () => ({
        backgroundColor: semantic.progress.fill,
        borderRadius: radius.sm,
      }),
    },
  },

  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => ({
        // No outer border — the surrounding layout provides containment.
        border: 'none',

        // Remove default cell/header focus outline; focus is managed at row level.
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          {
            outline: 'none',
          },

        // Hide the native scrollbar; only the virtual scroll is visible.
        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
          display: 'none',
        },

        // MUI X v9 stopped painting the grid body with a surface colour, so the
        // page bg showed through fillers / empty area. Paint grid surfaces paper.
        '& .MuiDataGrid-main, & .MuiDataGrid-filler, & .MuiDataGrid-filler--pinnedRight, & .MuiDataGrid-scrollbarFiller':
          {
            backgroundColor: theme.palette.background.paper,
          },

        // Clickable rows.
        '& .MuiDataGrid-row:hover': {
          cursor: 'pointer',
        },

        // Selected row background (QA: replaces legacy #E8EBF2).
        '& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: color.neutrals[100],
        },

        // Hide disabled checkboxes (blank boxes confuse users).
        '& .MuiDataGrid-row .MuiDataGrid-cellCheckbox .Mui-disabled': {
          display: 'none',
        },

        // MUI X v9 cells default to display:block; restore v6 flex-centred cells
        // so renderCell content with block children stays vertically centred.
        '& .MuiDataGrid-cell': {
          display: 'flex',
          alignItems: 'center',
          lineHeight: 'normal',
        },

        // Suppress v9 pinned-column separators (border + scroll shadow) to match
        // the pre-upgrade look.
        '& .MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-columnHeader--withLeftBorder, & .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withRightBorder':
          {
            borderLeft: 'none',
            borderRight: 'none',
          },
        '& .MuiDataGrid-scrollShadow--horizontal': {
          display: 'none',
        },

        // Column resize separators: hidden until header hover (v6 behaviour).
        '& .MuiDataGrid-columnSeparator': {
          opacity: 0,
        },
        '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator--resizable': {
          opacity: 1,
        },

        // Header title typography from tokens.
        '& .MuiDataGrid-columnHeaderTitle': {
          color: theme.palette.text.secondary,
          fontSize: fontSize[13],
          fontWeight: fontWeight.semibold,
        },

        // Native footer (pagination) divider.
        '& .MuiDataGrid-footerContainer': {
          borderTop: `1px solid ${theme.palette.divider}`,
        },
      }),
    },
  },
};
